"use client";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import React from "react";

interface LabelsLineProps {
  labels: string[];
}

// Maximum number used for indicator width measurement to ensure we reserve enough space
// for the (+X) indicator regardless of the actual number of hidden labels.
// This conservative upper bound prevents layout shifts when the actual count changes.
const MAX_INDICATOR_COUNT = 999;

// Pure utility function for creating unique keys to handle duplicate labels
const createUniqueKeys = (labelList: string[], prefix = "") => {
  const counts = new Map<string, number>();
  return labelList.map((text, index) => {
    const count = counts.get(text) ?? 0;
    counts.set(text, count + 1);
    return {
      key: `${prefix}${text}-${count}`,
      text,
      index,
    };
  });
};

interface LabelWidths {
  availableWidth: number;
  separatorWidth: number;
  indicatorWidth: number;
  labelWidths: number[];
}

/**
 * Returns how many leading labels fit on one line. When more labels follow,
 * the row must also leave room for a separator plus the "(+X)" indicator.
 */
function countLabelsThatFit({
  availableWidth,
  separatorWidth,
  indicatorWidth,
  labelWidths,
}: LabelWidths): number {
  let usedWidth = 0;
  for (let index = 0; index < labelWidths.length; index++) {
    const separator = index > 0 ? separatorWidth : 0;
    const hasMoreLabels = index < labelWidths.length - 1;
    const indicatorReserve = hasMoreLabels
      ? indicatorWidth + separatorWidth
      : 0;
    const rowWidth = usedWidth + separator + labelWidths[index];
    if (rowWidth + indicatorReserve > availableWidth) return index;
    usedWidth = rowWidth;
  }
  return labelWidths.length;
}

export function LabelsLine({ labels }: LabelsLineProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureContainerRef = React.useRef<HTMLDivElement>(null);

  const [visibleCount, setVisibleCount] = React.useState(0);
  const [isReady, setIsReady] = React.useState(false);

  const calculateVisibleLabels = React.useCallback(() => {
    const container = containerRef.current;
    const measureContainer = measureContainerRef.current;

    if (!container || !measureContainer) return;

    // Measurement children are laid out as [separator, indicator, ...labels]
    const widthOf = (child: Element | undefined) =>
      child?.getBoundingClientRect().width || 0;
    const children = Array.from(measureContainer.children);

    setVisibleCount(
      countLabelsThatFit({
        availableWidth: container.clientWidth,
        separatorWidth: widthOf(children[0]),
        indicatorWidth: widthOf(children[1]),
        labelWidths: labels.map((_, index) => widthOf(children[index + 2])),
      }),
    );
    setIsReady(true);
  }, [labels]);

  const debouncedCalculateVisibleLabels = React.useCallback(() => {
    let timeoutId: NodeJS.Timeout;

    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateVisibleLabels, 100);
    };
  }, [calculateVisibleLabels]);

  React.useLayoutEffect(() => {
    calculateVisibleLabels();

    const debouncedFn = debouncedCalculateVisibleLabels();
    const observer = new ResizeObserver(debouncedFn);

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [calculateVisibleLabels, debouncedCalculateVisibleLabels]);

  // Memoize visible and hidden labels to avoid recalculating when dependencies haven't changed
  const visibleLabels = React.useMemo(
    () => createUniqueKeys(labels.slice(0, visibleCount)),
    [labels, visibleCount],
  );

  const hiddenLabels = React.useMemo(
    () => createUniqueKeys(labels.slice(visibleCount)),
    [labels, visibleCount],
  );

  // Memoize measurement keys to avoid recalculating on every render
  const measurementKeys = React.useMemo(
    () => createUniqueKeys(labels, "measure-"),
    [labels],
  );

  if (!isReady) {
    return (
      <div
        className="text-gray-500 text-sm mt-0.5 flex items-center"
        ref={containerRef}
      >
        <div className="flex items-center space-x-1">
          <div className="h-3 w-12 bg-gray-200 rounded animate-pulse" />
          <span className="px-1">·</span>
          <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
          <span className="px-1">·</span>
          <div className="h-3 w-10 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Measurement container - hidden but used for width calculations */}
        <div
          ref={measureContainerRef}
          className="absolute -z-50 opacity-0 pointer-events-none select-none"
          aria-hidden="true"
        >
          <span className="px-1">·</span>
          <span className="whitespace-nowrap">(+{MAX_INDICATOR_COUNT})</span>
          {measurementKeys.map((item) => (
            <span key={item.key} className="whitespace-nowrap">
              {item.text}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="text-gray-500 text-sm mt-0.5 flex items-center"
      ref={containerRef}
    >
      {visibleLabels.map((item, i) => (
        <span key={item.key} className="whitespace-nowrap">
          {item.text}
          {i < visibleLabels.length - 1 && <span className="px-1">·</span>}
        </span>
      ))}

      {hiddenLabels.length > 0 && (
        <>
          {visibleLabels.length > 0 && <span className="px-1">·</span>}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="whitespace-nowrap cursor-help text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors min-w-6 min-h-6 inline-flex items-center justify-center"
                aria-label={`Show ${hiddenLabels.length} more labels`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                (+{hiddenLabels.length})
              </button>
            </TooltipTrigger>
            <TooltipContent
              sideOffset={8}
              className="max-w-xs whitespace-normal"
            >
              {hiddenLabels.map((item) => (
                <span key={item.key} className="block">
                  {item.text}
                </span>
              ))}
            </TooltipContent>
          </Tooltip>
        </>
      )}
    </div>
  );
}
